import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { verificationsApi } from "@/api/verifications";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

/**
 * UseCasesPage — /use-cases
 *
 * Dedicated page for the 9 V1 categories. Clicking "Use cases" in the
 * nav from any page lands here (instead of scrolling to a section that
 * only exists on the landing page).
 *
 * Each category card is self-contained and links straight to
 * /get-verified?category=KEY so the visitor lands in the submission
 * flow with their choice pre-selected.
 *
 * Honesty (2026-10-09): reviewers review; they do not represent the buyer,
 * file anything, accept liability or take on fiduciary duty. Copy saying so
 * was removed, as were per-category turnaround claims ("2–4h async") that
 * no tier offers. The real turnarounds are 24 hours, 48 hours or one week.
 *
 * Async-only launch sweep (2026-10-12): this page makes no claim about a
 * reviewer's licence or seniority, because the only reviewer today is the
 * founder and he holds no such licence. A card is open (with a Submit link)
 * only when a reviewer covers it today; the rest say "Not open yet" and link
 * nowhere. "Covers it today" comes from GET /verifications/categories, the
 * same list the Get Verified wizard offers; OPEN_FALLBACK is used only until
 * that answers, or if it fails.
 */

// Categories a reviewer covers today (founder: product, strategy, code review,
// ship-your-app). Mirrors the server's answer on 2026-10-09.
const OPEN_FALLBACK = ["product", "strategy", "code_review", "ship_app"];

const CATEGORIES = [
  {
    key: "law", label: "Law", icon: "⚖️",
    blurb: "AI drafts contracts. Xpert has no reviewer for legal work yet.",
    ai:     "Drafts NDAs, term sheets, employment agreements, immigration forms",
  },
  {
    key: "product", label: "Product", icon: "🎯",
    blurb: "AI drafts the PRD. Ankit Kumar, Xpert's founder, reads it and writes you a verdict.",
    ai:     "Generates PRDs, positioning, GTM plans, roadmap drafts",
    review: "A written verdict: findings, what to change, and how confident the call is.",
    examples: ["Roadmap review", "Positioning audit", "PRD critique", "Pricing analysis"],
  },
  {
    key: "engineering", label: "Engineering", icon: "⚙️",
    blurb: "AI designs the architecture. Not open yet. For code, see Code Reviews.",
    ai:     "Designs system architecture, selects stack, benchmarks alternatives",
  },
  {
    key: "finance", label: "Finance", icon: "💹",
    blurb: "AI models the math. Xpert has no reviewer for finance work yet.",
    ai:     "Builds projections, models portfolios, identifies tax savings",
  },
  {
    key: "fundraising", label: "Fundraising", icon: "🚀",
    blurb: "AI drafts your deck. Xpert has no reviewer for fundraising work yet.",
    ai:     "Drafts pitch decks, market sizing, investor narratives, FAQ docs",
  },
  {
    key: "accounting", label: "Accounting & Bookkeeping", icon: "📒",
    blurb: "AI categorises transactions. Xpert has no reviewer for accounting work yet.",
    ai:     "Categorises transactions, drafts journals, prepares tax filings",
  },
  {
    key: "strategy", label: "Strategy Reviews", icon: "🧭",
    blurb: "AI memos look great. Ankit Kumar, Xpert's founder, reads yours and writes you a verdict.",
    ai:     "Generates strategy memos, OKRs, org designs, market entries",
    review: "A written verdict: where the reasoning is thin, what to change, and how confident the call is.",
    examples: ["Strategy memo review", "OKR sanity-check", "Market entry plan", "Org design"],
  },
  {
    key: "code_review", label: "Code Reviews", icon: "💻",
    blurb: "Cursor and Copilot ship features fast. Ankit Kumar, Xpert's founder, reads the code and writes you a verdict.",
    ai:     "Cursor / Copilot ships features. AI lint catches the easy stuff.",
    review: "A written verdict: bugs, risks, what to refactor, and how confident the call is.",
    examples: ["Pull request review", "Refactor plan", "Security audit", "Performance review"],
  },
  {
    key: "ship_app", label: "Ship Your Vibe-Coded App", icon: "🚢",
    blurb: "You built a working prototype with Cursor / Lovable / v0. Ankit Kumar, Xpert's founder, reviews what stands between it and production.",
    ai:     "You built it in Cursor / Lovable / v0. It works on your laptop.",
    review: "A written verdict on production readiness (security, deploy, data) with a fix list.",
    examples: ["Production readiness audit", "Auth + payments hardening", "Deploy + DNS", "Infra setup"],
  },
];

export default function UseCasesPage() {
  const [openKeys, setOpenKeys] = useState(OPEN_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    verificationsApi.getCategories()
      .then((data) => {
        if (cancelled || !Array.isArray(data?.categories)) return;
        setOpenKeys(data.categories.map((c) => c.key));
      })
      .catch(() => { /* keep the fallback list */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />

      {/* Hero */}
      <section className="border-b border-slateline-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          {/* CLAUDE.md Rule §5 — canonical eyebrow line. "Use cases"
              labeling is implicit in the page URL + nav active state. */}
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green mb-4">
            The human review layer for AI-built work
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-4xl">
            AI is brilliant in every domain.<br />
            <span className="italic text-brand-green">Every domain has a signing problem.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slateline-600 max-w-2xl leading-relaxed">
            Each of these is a place where AI's answer isn't enough and you
            want a person's judgment before you ship. Today Ankit Kumar,
            Xpert's founder, reviews AI-built code and product and strategy
            work. The rest are marked "Not open yet" until a reviewer covers
            them.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/get-verified"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md
                         bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors"
            >
              Submit AI output for review
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Category cards */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c) => {
              const isOpen = openKeys.includes(c.key);
              return (
              <article
                key={c.key}
                className={[
                  "group bg-canvas rounded-xl border border-slateline-200 p-6 transition-all flex flex-col",
                  isOpen ? "hover:border-ink/20 hover:shadow-md" : "opacity-80",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <span className="text-3xl leading-none">{c.icon}</span>
                  <div className="min-w-0">
                    <h2 className="font-display text-xl text-ink leading-tight">{c.label}</h2>
                  </div>
                </div>

                <p className="mt-4 text-sm text-slateline-700 leading-relaxed">
                  {c.blurb}
                </p>

                <div className="mt-5 space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slateline-400 font-bold mb-1">
                      AI does
                    </div>
                    <div className="text-slateline-600 leading-snug">{c.ai}</div>
                  </div>
                  {isOpen && c.review && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-brand-green font-bold mb-1">
                        You get
                      </div>
                      <div className="text-ink leading-snug font-medium">{c.review}</div>
                    </div>
                  )}
                </div>

                {isOpen && c.examples && (
                  <div className="mt-5">
                    <div className="text-[10px] uppercase tracking-wider text-slateline-400 font-bold mb-2">
                      Examples
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {c.examples.map((e) => (
                        <span key={e}
                              className="text-[11px] font-medium text-slateline-600
                                         bg-white border border-slateline-200 rounded-full px-2.5 py-0.5">
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-slateline-200 flex items-center justify-between">
                  {isOpen ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-green
                                       bg-brand-greenTint border border-brand-green/30 rounded-full px-2.5 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                        24h · 48h · 1 week
                      </span>
                      <Link
                        to={`/get-verified?category=${c.key}`}
                        className="text-sm font-semibold text-brand-green hover:text-brand-greenHover inline-flex items-center gap-1"
                      >
                        Submit
                        <span className="transition-transform group-hover:translate-x-0.5">→</span>
                      </Link>
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slateline-500
                                     bg-slateline-100 border border-slateline-200 rounded-full px-2.5 py-1">
                      Not open yet
                    </span>
                  )}
                </div>
              </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-ink text-white relative overflow-hidden">
        <div aria-hidden="true"
             className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-green/30 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">
            Your category not listed?
          </h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            We add hand-picked specialists as demand grows. Tell us what you
            need reviewed.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/enterprise"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md
                         bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors"
            >
              Talk to us
              <span aria-hidden="true">→</span>
            </Link>
            <Link
              to="/become-expert"
              className="inline-flex items-center justify-center h-12 px-6 rounded-md
                         border border-white/25 hover:border-white/50 text-white font-medium text-sm transition-colors"
            >
              Apply as an expert
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
